import React, { memo, useCallback, useMemo, useState } from 'react';

import AttachDocuments from '@components/common/AttachDocuments';

import { useMutateCreateUploadUrlGoldenSettings } from '@hooks/goldenSettings/useMutateCreateUploadUrlGoldenSettings';
import { useMutateGoldenSettingsImport } from '@hooks/goldenSettings/useMutateGoldenSettingsImport';

import { readLoadedFile } from '@utils/fileUtils';

import { TGoldenSettingsSetTypeDTO } from '@interfaces/cmModule/dto/goldenSettingsDTO';
import { IUploadUrlType } from '@interfaces/import/import';

import {
	acceptedFileTypesJson,
	acceptedFileTypesXML,
	fileErrorsJson,
	fileErrorsXML,
	MAX_BYTES,
	supportsTextJson,
	supportsTextXML,
} from './constants';

import { IGoldenSettingsImportFile } from '../GoldenSettingsForm';

export type TSetGoldenSettingsConfig = (data: Partial<IGoldenSettingsImportFile>) => void;

interface AttachGoldenSettingsProps {
	toggleUploadFilesModal: () => void;
	setGoldenSettingsConfig: TSetGoldenSettingsConfig;
	params: TGoldenSettingsSetTypeDTO;
	isMavenirVendor: boolean;
}

export interface IGoldenSettingsImportedFile {
	name: string;
	importId: string;
	file: File;
}

const AttachGoldenSettings = ({
	toggleUploadFilesModal,
	setGoldenSettingsConfig,
	params,
	isMavenirVendor,
}: AttachGoldenSettingsProps) => {
	const [settingsConfig, setSettingsConfig] = useState<Partial<IGoldenSettingsImportFile>[]>([]);
	const [importedFile, setImportedFile] = useState<IGoldenSettingsImportedFile | undefined>();

	const {
		createUploadUrlAttachment,
		progress,
		isLoadingCreateUploadUrlAttachment,
	} = useMutateCreateUploadUrlGoldenSettings(setImportedFile);

	const {
		importGoldenSettings,
		isLoadingImportGoldenSettings,
	} = useMutateGoldenSettingsImport(params, setGoldenSettingsConfig);

	const importHandler = useCallback(() => {
		if (!importedFile) {
			return;
		}

		importGoldenSettings(importedFile);
		toggleUploadFilesModal();
	}, [importedFile, importGoldenSettings, toggleUploadFilesModal]);

	const readUploadedFile = useCallback(() => {
		if (!settingsConfig) {
			return;
		}

		const { importFileName, rawData, tree } = settingsConfig[0];

		setGoldenSettingsConfig({
			importFileName,
			rawData,
			tree,
		});

		toggleUploadFilesModal();
	}, [settingsConfig, setGoldenSettingsConfig, toggleUploadFilesModal]);

	const setFileContent = useCallback(async ({ files }: IUploadUrlType) => {
		if (!files.length) {
			return;
		}

		const result = await readLoadedFile(files[0]);

		if (typeof result === 'string') {
			setSettingsConfig([{
				importFileName: files[0].name,
				rawData: result,
				tree: undefined,
			}]);
		}
	}, [setSettingsConfig]);

	const clearUploadedFiles = useCallback(() => {
		setSettingsConfig([]);
		setImportedFile(undefined);
		setGoldenSettingsConfig({
			importFileName: undefined,
			rawData: undefined,
			tree: undefined,
		});
	}, [setGoldenSettingsConfig]);

	const onUploadClickHandler = useCallback(() => {
		return isMavenirVendor ? importHandler() : readUploadedFile();
	}, [importHandler, isMavenirVendor, readUploadedFile]);

	const createAttachment = useCallback(({ files }: IUploadUrlType) => {
		return isMavenirVendor
			? createUploadUrlAttachment({ files })
			: setFileContent({ files });
	}, [createUploadUrlAttachment, isMavenirVendor, setFileContent]);

	const filteredFiles = useMemo(() => {
		return isMavenirVendor
			? importedFile
				? [importedFile.file]
				: []
			: undefined;
	}, [importedFile, isMavenirVendor]);

	return (
		<AttachDocuments
			acceptedFileTypes={isMavenirVendor ? acceptedFileTypesXML : acceptedFileTypesJson}
			createUploadUrlAttachment={createAttachment}
			fileErrors={isMavenirVendor ? fileErrorsXML : fileErrorsJson}
			fileRejectionErrorHandler={clearUploadedFiles}
			filteredFiles={filteredFiles}
			isMultipleFiles={false}
			isUploading={isLoadingCreateUploadUrlAttachment || isLoadingImportGoldenSettings}
			maxBytes={MAX_BYTES}
			onDropAcceptedHandler={clearUploadedFiles}
			onUploadClickHandler={onUploadClickHandler}
			progress={isMavenirVendor ? progress : undefined}
			supportsText={isMavenirVendor ? supportsTextXML : supportsTextJson}
			toggleUploadFilesModal={toggleUploadFilesModal}
			uploadedFilesLength={isMavenirVendor ? Number(!!importedFile) : settingsConfig?.length}
		/>
	);
};

export default memo(AttachGoldenSettings);
